import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PageHero from '@/components/shared/PageHero';
import { publicApi } from '@/lib/api';
import { Download, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import useSEO from '@/hooks/useSEO';

export default function DownloadsPage() {
    const [items, setItems] = useState([]);
    useSEO({
        title: 'Downloads',
        description:
            'Download VVVSI Trust brochure, annual reports, CSR partnership deck, and compliance certificates (80G, 12A, CSR-1). All documents available for grants, CSR reviews and public reference.',
    });
    useEffect(() => {
        publicApi.getDownloads().then(setItems).catch(() => {});
    }, []);

    return (
        <div data-testid="downloads-page">
            <PageHero
                eyebrow="Downloads"
                title="Brochures, reports and compliance documents."
                subtitle="Access the full VVVSI collateral for grants, CSR reviews and public information."
            />
            <section className="section-pad">
                <div className="container-page grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.map((d, i) => (
                        <motion.div
                            key={d.id}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            viewport={{ once: true, margin: '-40px' }}
                        >
                            <Card className="h-full bg-card border-border card-lift">
                                <CardContent className="p-6 flex flex-col h-full">
                                    <div className="h-12 w-12 rounded-xl bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] flex items-center justify-center mb-4">
                                        <FileText className="h-6 w-6" />
                                    </div>
                                    <Badge variant="outline" className="text-[10px] mb-2 w-fit">
                                        {d.category}
                                    </Badge>
                                    <div className="font-display font-semibold text-lg mb-1">
                                        {d.title}
                                    </div>
                                    {d.description && (
                                        <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                                            {d.description}
                                        </p>
                                    )}
                                    <div className="mt-auto flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground">
                                            {d.file_size}
                                        </span>
                                        <a
                                            href={d.file_url}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            data-testid={`download-item-${d.id}`}
                                        >
                                            <Button
                                                size="sm"
                                                className="bg-[hsl(var(--primary))] text-primary-foreground hover:bg-[hsl(var(--primary))]/90"
                                            >
                                                <Download className="h-4 w-4 mr-1.5" /> Download
                                            </Button>
                                        </a>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </section>
        </div>
    );
}
